import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as XLSX from "xlsx";
import axios from "../../api/axios";

import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import InputFilter from "../GeneralComponents/InputFilter";
import InputFile from "../GeneralComponents/InputFile";

import { PERIODS } from "../../Constants/Constants";

// ✅ Normaliza a "YYYY-MM-01"
const ensurePeriodo01 = (p) => {
  if (p == null) return "";
  const s = String(p).trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;

  const m = s.match(/^(\d{4}-\d{2})/);
  if (m) return `${m[1]}-01`;

  return s;
};

const currentMonthYYYYMM01 = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
};

export default function GenericTextList({
  title = "Listado",
  columns = [],
  filtersConfig = [],
  dataSource,
  api,
  selectable = true,
  breadcrumb,

  // ✅ NUEVO (opcional): si viene, se renderiza esta vista en vez de tabla+filtros
  renderView, // (ctx) => ReactNode
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({});
  const [tableData, setTableData] = useState([]);

  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);

  const [selected, setSelected] = useState([]);

  // --- Periodo: usa PERIODS y default mes actual con -01 ---
  const periodoFilterCfg = useMemo(
    () => filtersConfig.find((f) => f?.item === "periodo"),
    [filtersConfig]
  );

  const monthNow01 = useMemo(() => currentMonthYYYYMM01(), []);

  const defaultPeriod = useMemo(() => {
    const periods = (PERIODS || []).map(ensurePeriodo01);
    const found = periods.find((p) => p === monthNow01);
    const fallback = ensurePeriodo01(PERIODS?.[1] ?? PERIODS?.[0] ?? monthNow01);
    return found ?? fallback;
  }, [monthNow01]);

  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);

  useEffect(() => {
    if (defaultPeriod && selectedPeriod !== defaultPeriod) {
      setSelectedPeriod(defaultPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPeriod]);

  const buildApiWithPeriodo = (baseApi, periodo) => {
    if (!baseApi || !periodo) return baseApi;
    const sep = baseApi.includes("?") ? "&" : "?";
    return `${baseApi}${sep}periodo=${encodeURIComponent(String(periodo))}`;
  };

  const applyFilters = (nextFilters, baseRows = rows) => {
    const activeEntries = Object.entries(nextFilters).filter(
      ([, val]) => val != null && String(val).length > 0
    );

    const filtered =
      activeEntries.length === 0
        ? baseRows
        : baseRows.filter((row) =>
            activeEntries.every(
              ([key, val]) => String(row[key] ?? "") === String(val)
            )
          );

    setTableData(filtered);
    setSelected([]);
    const maxPage = Math.floor(Math.max(filtered.length - 1, 0) / rpp);
    if (page > maxPage) setPage(0);
  };

  const handleFilter = (item, value) => {
    if (item === "periodo") {
      const v = ensurePeriodo01(value);
      if (v && String(v).length > 0) setSelectedPeriod(v);
      return;
    }

    const nf = { ...filters, [item]: value };
    if (value == null || String(value).length === 0) {
      delete nf[item];
    }
    setFilters(nf);
    applyFilters(nf);
  };

  // --- Load data (depende del periodo) ---
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        let data = [];

        if (typeof dataSource === "function") {
          data = await dataSource();
        } else if (typeof api === "string" && api.length > 0) {
          const apiWithPeriodo =
            periodoFilterCfg ? buildApiWithPeriodo(api, selectedPeriod) : api;
          const resp = await axios.getReport(apiWithPeriodo);
          data = resp.data || [];
        }

        if (alive) {
          const normalized = data.map((r, idx) => ({
            id: r.id ?? idx,
            index: idx,
            ...r,
          }));
          setRows(normalized);
          setTableData(normalized);

          setSelected([]);
          setPage(0);
          setFilters({});
        }
      } catch (e) {
        if (alive) {
          console.error("Error cargando datos:", e);
          setRows([]);
          setTableData([]);
          setSelected([]);
          setPage(0);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [dataSource, api, selectedPeriod, periodoFilterCfg]);

  const isSelected = (idx) => selected.includes(idx);

  const handleSelectAll = (e) => {
    if (!selectable) return;
    setSelected(e.target.checked ? tableData.map((r) => r.index) : []);
  };

  const handleCheckboxClick = (idx) => {
    if (!selectable) return;
    setSelected((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const downloadXLS = () => {
    const selectedRows = tableData.filter((row) => selected.includes(row.index));
    const prepared = selectedRows.map((row) => {
      const obj = {};
      columns.forEach((c) => {
        obj[c.label] = row[c.id] ?? "";
      });
      return obj;
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(prepared);
    XLSX.utils.book_append_sheet(wb, ws, "Tabla");
    XLSX.writeFile(wb, (title || "tabla") + ".xlsx");
  };

  const [evError, setEvError] = useState(false);

  const [reportFile, setReportFile] = useState();

  const setFile = (archivo) => {
    setEvError(!archivo ? true : false);
    setReportFile((prevData) => ({
      ...prevData,
      archivo,
    }));
  };

  // ✅ NUEVO: si el padre entrega renderView, se usa esa vista (sin tabla ni filtros)
  if (typeof renderView === "function") {
    return renderView({
      rows,
      tableData,
      loading,
      filters,
      selected,
      selectedPeriod,
      setSelectedPeriod,
    });
  }

  // ---- ORIGINAL (sin cambios) ----
  return (
    <>
      {filtersConfig.length > 0 && (
        <Grid container spacing={0} sx={{ m: 0, p: 0 }} alignItems="flex-start">
          <Grid size={12}>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {filtersConfig.map(({ item, name, size = 6, opt, defaultOpt }) => (
                <Grid key={item} size={size}>
                  <InputFilter
                    data={rows}
                    item={item}
                    name={name}
                    callback={handleFilter}
                    opt={item === "periodo" ? PERIODS : opt}
                    defaultOpt={defaultOpt}
                    value={item === "periodo" ? PERIODS[0] : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {title === "OTROS REPORTES" ? (
            <Paper sx={{ mb: 2, p: 1.25, textAlign: "center" }}>
              <Box sx={{ p: "2rem 9rem", textAlign: "center" }}>
                {breadcrumb ? (
                  <Box sx={{ display: "inline-block", textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        fontSize: "1.08rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {breadcrumb}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        borderBottom: "1px solid #111",
                        mt: 0.75,
                      }}
                    />
                  </Box>
                ) : null}

                <Stack alignItems="left" justifyContent="center" spacing={2}>
                  <Typography
                    variant="h1"
                    color="inherit"
                    noWrap
                    sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}
                  >
                    OTROS REPORTES
                  </Typography>

                  <Box
                    justifyContent="center"
                    sx={{ maxWidth: "100%", margin: "0 auto", display: "flex" }}
                  >
                    <Typography variant="body" align="center" sx={{ marginBottom: "2rem" }}>
                      En el siguiente campo cargue el archivos con la información
                      que necesite reportar para dejar en los registros.
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Box>
                      <InputFile
                        onFileSelect={setFile}
                        fileType="pdf"
                        placeholder="Oficio recepción de obras"
                        buttonText="Subir archivo"
                        sizeBtnGrid={4}
                      />
                      <Typography
                        variant="small"
                        align="left"
                        sx={{
                          marginBottom: "18px",
                          color: evError ? "red" : "#111",
                        }}
                      >
                        {evError ? "Debe adjuntar evidencia" : ""}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          ) : null}

          <Paper sx={{ mb: 2, p: 1.25, textAlign: "center" }}>
            {breadcrumb && title !== "OTROS REPORTES" ? (
              <Box sx={{ display: "inline-block", textAlign: "center", mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "1.08rem",
                    lineHeight: 1.2,
                  }}
                >
                  {breadcrumb}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    borderBottom: "1px solid #111",
                    mt: 0.75,
                  }}
                />
              </Box>
            ) : null}

            <Typography variant="h1" color="inherit" noWrap sx={{ textAlign: "center", p: "0.5rem" }}>
              {title} {title === "OTROS REPORTES" ? "ANTERIORES" : ""}
            </Typography>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectable && (
                    <TableCell align="left" sx={{ width: 48 }}>
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < tableData.length}
                        checked={tableData.length > 0 && selected.length === tableData.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align || "left"} sx={{ minWidth: col.minWidth }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData.slice(page * rpp, page * rpp + rpp).map((row) => (
                  <TableRow key={row.id} hover={!!selectable} selected={isSelected(row.index)}>
                    {selectable && (
                      <TableCell>
                        <Checkbox
                          checked={isSelected(row.index)}
                          onClick={() => handleCheckboxClick(row.index)}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        {row[col.id] ?? ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {tableData.length === 0 && (
              <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                Sin datos
              </Box>
            )}

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 100]}
              count={tableData.length}
              rowsPerPage={rpp}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRpp(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>

          {selectable && (
            <Grid container spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <PageButtonWhite onClick={downloadXLS} width="100%" disabled={selected.length === 0}>
                  Descargar selección (.xls)
                </PageButtonWhite>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </>
  );
}
